/**
 * Mock data for the Home dashboard ("Business Optimization Score").
 *
 * Temporary — exists only while the page is built ahead of the API. When the
 * real endpoints land, replace the page's `buildHomeMetrics(interval, variant)`
 * with a selector keyed on the active brand that yields this same `HomeData`
 * shape, drop the `variant` plumbing, and delete this file.
 *
 * A faithful port of the source dashboard's `buildMetrics` generator. Each
 * interval buckets its trends at a different cohort granularity — 24H hourly,
 * 7D daily, 30D weekly, 90D monthly — so switching the filter re-buckets every
 * chart (point count *and* labels), not just its axis ticks. `7D` reproduces the
 * canonical Figma headline figures; the other intervals derive
 * deterministic-but-distinct datasets (seeded RNG) so the KPIs and charts
 * animate when switching. Colours are NOT here — gauge hues resolve from
 * `HOME_GAUGE_COLORS` / `HOME_GAUGE_GLOW` via each gauge `id`, and the chart
 * series colours from `HOME_SERIES_COLORS` in the sections.
 *
 * Per-brand mock: since there is no brand-scoped API yet, five brand
 * "profiles" (`HOME_BRAND_VARIANTS`) re-skin the same generator into five
 * visibly-distinct datasets the page cycles through as the brand switcher
 * changes. `variant` 0 is the canonical Figma brand and is byte-identical to
 * the pre-brand output. Delete the profiles alongside this file once the API
 * provides real per-brand metrics.
 */
import type { HomeData, HomeInterval, HomeStat } from './types';
import type { IconName } from '../components/Icon/Icon';
import { gaugeStatusLabel, type HomeGaugeId } from '../theme/tokens';

/**
 * Per-interval cohort granularity. `points` is the number of buckets each chart
 * draws (24H hourly → 24, 7D daily → 7, 30D weekly → 4, 90D monthly → 3), so the
 * line genuinely re-samples when the filter changes — not just its axis ticks.
 * `axisLabels` are the sparse ticks under the line; `pointLabels` name every
 * individual sample in the hover readout and are only needed when a chart carries
 * more points than ticks (24H: 24 points, 6 ticks). The other intervals draw one
 * tick per point, so they leave `pointLabels` empty and the chart maps the hover
 * heading straight onto `axisLabels`.
 */
type IntervalMeta = {
  seed: number;
  scale: number;
  /** Cohort bucket count = data points drawn (hourly 24 / daily 7 / weekly 4 / monthly 3). */
  points: number;
  /** Sparse x-axis tick labels. */
  axisLabels: string[];
  /** Per-sample hover headings; `[]` when one tick already names each point. */
  pointLabels: string[];
  /** Product / Acquisition / Operations score for this interval. */
  gauges: [number, number, number];
};

// The 24 hourly headings for the hourly (Today / Yesterday) hover readout (the axis only labels every 4th).
const HOURS_24: string[] = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

const META: Record<HomeInterval, IntervalMeta> = {
  TODAY: { seed: 11, scale: 0.18, points: 24, axisLabels: ['00', '04', '08', '12', '16', '20'], pointLabels: HOURS_24, gauges: [78, 58, 92] },
  YESTERDAY: { seed: 17, scale: 0.95, points: 24, axisLabels: ['00', '04', '08', '12', '16', '20'], pointLabels: HOURS_24, gauges: [80, 60, 93] },
  '7D': { seed: 27, scale: 1, points: 7, axisLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], pointLabels: [], gauges: [84, 64, 96] },
  '30D': { seed: 53, scale: 4.3, points: 4, axisLabels: ['W1', 'W2', 'W3', 'W4'], pointLabels: [], gauges: [88, 71, 94] },
  '90D': { seed: 91, scale: 12.5, points: 3, axisLabels: ['Apr', 'May', 'Jun'], pointLabels: [], gauges: [81, 67, 90] },
  '6M': { seed: 131, scale: 25, points: 6, axisLabels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'], pointLabels: [], gauges: [85, 69, 91] },
  '1Y': { seed: 167, scale: 52, points: 12, axisLabels: ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'], pointLabels: [], gauges: [83, 66, 93] },
};

/** Which of the {@link HOME_BRAND_VARIANTS} datasets a brand renders. */
export type HomeBrandVariant = 0 | 1 | 2 | 3 | 4;

type BrandProfile = {
  /** Added to the interval seed so each brand gets a distinct RNG stream (and
   *  thus distinct chart shapes / generated KPIs). */
  seedShift: number;
  /** Multiplies every KPI/chart magnitude — a bigger or smaller book. */
  mult: number;
  /** Per-gauge offset (Product / Acquisition / Operations), clamped to 0–100;
   *  the status band re-derives from the result. */
  gaugeShift: [number, number, number];
};

/**
 * Five stand-in brand datasets. The page maps the active brand to one of these
 * (cycling) until a real per-brand API lands. Profile 0 is the canonical Figma
 * brand: `seedShift 0` + `mult 1` + no gauge shift make its output identical to
 * the pre-brand single dataset, so the default view is unchanged.
 *
 * Count kept in lock-step with `ACQ_BRAND_VARIANTS` (also 5) so a brand maps to
 * the same profile index on both pages — the Home "Acquisition" gauge (which
 * mirrors the acquisition score) then stays consistent with the rest of this
 * brand's Home data instead of wrapping at a different modulo.
 */
export const HOME_BRAND_VARIANTS: readonly BrandProfile[] = [
  { seedShift: 0, mult: 1, gaugeShift: [0, 0, 0] },
  { seedShift: 1009, mult: 1.42, gaugeShift: [6, 13, -4] },
  { seedShift: 2017, mult: 0.63, gaugeShift: [-11, -7, 3] },
  { seedShift: 3023, mult: 0.92, gaugeShift: [-4, 5, 8] },
  { seedShift: 4099, mult: 1.18, gaugeShift: [9, -6, -2] },
];

type ChannelSeed = { id: string; name: string; icon: IconName; base: number };

const CHANNELS: ChannelSeed[] = [
  { id: 'web', name: 'Web', icon: 'globe', base: 1420 },
  { id: 'apps', name: 'Apps', icon: 'mobile', base: 890 },
  { id: 'crypto', name: 'Crypto', icon: 'crypto', base: 612 },
  { id: 'short', name: 'Short Videos', icon: 'video', base: 612 },
  { id: 'affiliates', name: 'Affiliates', icon: 'affiliates', base: 612 },
  { id: 'influencers', name: 'Influencers', icon: 'influencers', base: 612 },
];

const GAUGES: { id: HomeGaugeId; label: string }[] = [
  { id: 'product', label: 'Product' },
  { id: 'acq', label: 'Acquisition' },
  { id: 'ops', label: 'Operations' },
];

/** Mulberry32 — deterministic, seeded PRNG (no Date/Math.random). */
function rng(seed: number): () => number {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A gently trending random walk, floored so it never collapses to zero. */
function wave(r: () => number, n: number, base: number, amp: number, trend: number): number[] {
  const pts: number[] = [];
  let v = base;
  for (let i = 0; i < n; i++) {
    v += (r() - 0.42) * amp + trend;
    pts.push(Math.max(base * 0.2, v));
  }
  return pts;
}

/**
 * Score band label shown under each gauge number — delegates to the shared
 * {@link gaugeStatusLabel} (Optimal > 80, Sustainable > 60, else Poor) so every
 * Home gauge bands its label exactly like the Acquisition gauge.
 */
function scoreStatus(v: number): string {
  return gaugeStatusLabel(v);
}

/**
 * Build the whole home dashboard dataset for an interval and brand `variant`.
 * Every chart is drawn at the interval's cohort granularity (`m.points`): 24H
 * hourly, 7D daily, 30D weekly, 90D monthly. The canonical `7D` / `variant` 0
 * headline figures and deltas reproduce the Figma values exactly (they don't draw
 * from the RNG); the chart shapes are seeded and follow the cohort point count.
 * The order of the literal below is intentional — do not reorder the `wave()` /
 * `delta()` calls (they share one RNG stream).
 *
 * `variant` (0–2, default 0) selects a {@link HOME_BRAND_VARIANTS} profile so a
 * brand switch yields a different dataset. Profile 0 leaves seed/scale/gauges
 * untouched, so the default brand's headline figures match the pre-brand output.
 */
export function buildHomeMetrics(interval: HomeInterval, variant: HomeBrandVariant = 0): HomeData {
  const m = META[interval];
  const profile = HOME_BRAND_VARIANTS[variant] ?? HOME_BRAND_VARIANTS[0];
  const r = rng(m.seed + profile.seedShift);
  const s = m.scale * profile.mult;
  // Canonical fixed deltas/multipliers only hold for the default brand on 7D;
  // every other brand/interval is fully generated so the datasets read distinct.
  const canonical = interval === '7D' && variant === 0;
  const clampScore = (n: number): number => Math.max(0, Math.min(100, Math.round(n)));
  const round = (x: number): number => Math.round(x);
  const delta = (): number => round((r() * 30 - 4) * 10) / 10;

  const regBase = 200 * s;
  const ftdBase = 124 * s;

  const stat = (value: number, d: number, prefix?: string, suffix?: string): HomeStat => ({
    value,
    delta: d,
    prefix,
    suffix,
  });

  return {
    gauges: GAUGES.map((g, i) => {
      const value = clampScore(m.gauges[i] + profile.gaugeShift[i]);
      return { id: g.id, label: g.label, value, status: scoreStatus(value) };
    }),

    acquisition: {
      registrations: stat(round(regBase), canonical ? 12 : delta()),
      ftds: stat(round(ftdBase), canonical ? 6 : delta()),
      xLabels: m.axisLabels,
      pointLabels: m.pointLabels,
      chart: {
        registrations: wave(r, m.points, regBase * 0.7, regBase * 0.45, regBase * 0.045),
        ftds: wave(r, m.points, ftdBase * 0.7, ftdBase * 0.4, ftdBase * 0.03),
      },
      days: {
        registrations: { prev: round(regBase * 0.92), current: round(regBase) },
        ftds: { prev: round(ftdBase * 0.9), current: round(ftdBase) },
      },
    },

    financial: {
      grossRevenue: stat(round(84500 * s), canonical ? 8.7 : delta(), '$'),
      stakes: stat(round(12 * s), delta(), '$', 'K'),
      wins: stat(round(42 * s), delta(), '$', 'K'),
      xLabels: m.axisLabels,
      pointLabels: m.pointLabels,
      chart: {
        ggr: wave(r, m.points, 700 * s, 360 * s, 34 * s),
        stakes: wave(r, m.points, 520 * s, 260 * s, 18 * s),
        wins: wave(r, m.points, 320 * s, 180 * s, 10 * s),
      },
      days: {
        ggr: { prev: round(700 * s * 0.92), current: round(700 * s) },
        stakes: { prev: round(520 * s * 0.9), current: round(520 * s) },
        wins: { prev: round(320 * s * 0.88), current: round(320 * s) },
      },
    },

    moneyFlow: {
      deposits: stat(round(169000 * s), canonical ? 8.7 : delta(), '$'),
      withdrawals: stat(round(12 * s), delta(), '$', 'K'),
      playerBalance: stat(round(42 * s), delta(), '$', 'K'),
    },

    channelDates: m.axisLabels,
    channelPointLabels: m.pointLabels,

    channels: CHANNELS.map((c) => ({
      id: c.id,
      name: c.name,
      icon: c.icon,
      registrations: round(c.base * s * (canonical ? 1 : 0.85 + r() * 0.3)),
      ftds: round(120 * s * (canonical ? 1 : 0.7 + r() * 0.6)),
      delta: canonical ? 22 : delta(),
      chart: {
        registrations: wave(r, m.points, c.base * 0.6, c.base * 0.4, c.base * 0.03),
        ftds: wave(r, m.points, c.base * 0.32, c.base * 0.22, c.base * 0.012),
        ftdValue: wave(r, m.points, c.base * 0.18, c.base * 0.14, c.base * 0.008),
      },
      days: {
        registrations: { prev: round(c.base * s * 0.5), current: round(c.base * s * 0.6) },
        ftds: { prev: round(120 * s * 0.5), current: round(120 * s * 0.6) },
        ftdValue: { prev: round(c.base * 0.15), current: round(c.base * 0.18) },
      },
    })),
  };
}

export default buildHomeMetrics;
